import { SearchOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Form, Select, Space, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ProjectLinkedDailyReportsSection } from '@/pages/admin/projects/ProjectLinkedDailyReportsSection';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectListItem } from '@/types/project';

interface FilterFormValues {
  projectId?: string;
}

export function ProjectReportManagementPage() {
  const { message } = App.useApp();
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectListItem | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      const result = await projectsApi.fetchProjects({ page: 1, pageSize: 200 });
      setProjects(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目列表失败';
      message.error(text);
      setProjects([]);
    }
  }, [message]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const handleSearch = (values: FilterFormValues) => {
    if (!values.projectId) {
      setSelectedProject(null);
      return;
    }
    const project = projects.find((item) => item.id === values.projectId) ?? null;
    setSelectedProject(project);
  };

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        项目日报
      </Typography.Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="统一日报模型"
        description="项目日报不再单独创建。成员在小程序「写日报」时关联项目后，记录会自动出现在对应项目下；本页按项目与日期查看。"
      />

      <Card style={{ marginBottom: 16 }}>
        <Form form={filterForm} layout="inline" onFinish={handleSearch}>
          <Form.Item
            name="projectId"
            label="项目"
            rules={[{ required: true, message: '请选择项目' }]}
          >
            <Select
              showSearch
              placeholder="请选择项目"
              style={{ width: 320 }}
              optionFilterProp="label"
              options={projects.map((item) => ({
                value: item.id,
                label: `${item.name}（${item.projectNo}）`,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields();
                  setSelectedProject(null);
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {selectedProject ? (
        <Card title={`${selectedProject.name} · 项目日报`}>
          <ProjectLinkedDailyReportsSection
            projectId={selectedProject.id}
            projectName={selectedProject.name}
            showHint={false}
          />
        </Card>
      ) : (
        <Card>
          <Typography.Text type="secondary">请先选择项目后查询。</Typography.Text>
        </Card>
      )}
    </div>
  );
}
